// @flow

import type { UserApiClientInterface } from './user-api-client-interface'
import type { User } from '../types/user'
import type { ValidationResult } from '../types/validation-result'
import type { Address } from '../types/address'
import type { CustomerDetails } from '../types/customer-details'
import { LoginResult } from '../types/login-result'
import {
  VALUE_IS_INVALID,
  EMAIL_INVALID_FORMAT_ERROR,
  VALUE_SHOULD_NOT_BE_BLANK, EMAIL_MX_CHECK_FAILED_ERROR, EMAIL_HOST_CHECK_FAILED_ERROR
} from './user-api-client-constants'
import type { AccountCredentials } from '../types/account-credentials'
import type {CustomerCards} from '../types/customer-cards'

class MockUserApiClient implements UserApiClientInterface {
  baseUrl: string

  constructor (baseUrl: string) {
    this.baseUrl = baseUrl
  }

  async getUser (): User {
    const user = {
      'username': 'jordy.versmissen@sqills.com',
      'email': 'jordy.versmissen@sqills.com',
      'profile': {
        'firstName': 'T.',
        'surname': 'Test',
        'surnamePrefix': 'van der',
        'salutation': 'M',
        'emailAddress': 'jordy.versmissen@sqills.com',
        'phoneNumber': '+31612345678',
        'bankAccountNumber': 'NL 71 ABNA0 456431036',
        'address': {
          'street': 'Hengelosestraat',
          'houseNumber': '52',
          'houseNumberExtension': null,
          'postalCode': '7514AJ',
          'city': 'Enschede',
          'countryCode': 'NLD'
        }
      }
    }
    return user
  }

  async updateUser (user: User): Promise<ValidationResult> {
    const errors = []
    if (user.profile.firstName.length === 0) {
      errors.push({
        code: VALUE_SHOULD_NOT_BE_BLANK,
        propertyPath: 'profile.firstName',
        message: 'This value should not be blank'
      })
    }
    if (!RegExp('^[A-z0-9\\s\\.]*$').test(String(user.profile.surnamePrefix))) {
      errors.push(
        {
          code: VALUE_IS_INVALID,
          propertyPath: 'profile.surnamePrefix',
          message: 'This value is not valid'
        }
      )
    }
    if (!user.profile.salutation) {
      errors.push({
        code: VALUE_SHOULD_NOT_BE_BLANK,
        propertyPath: 'profile.salutation',
        message: 'This value should not be blank'
      })
    }
    if (!user.profile.emailAddress) {
      errors.push({
        code: VALUE_SHOULD_NOT_BE_BLANK,
        propertyPath: 'profile.emailAddress',
        message: 'This value should not be blank'
      })
    } else {
      if (!user.profile.emailAddress.includes('@')) {
        errors.push({
          code: EMAIL_INVALID_FORMAT_ERROR,
          propertyPath: 'profile.emailAddress',
          message: 'This value is not a valid email address.'
        })
      } else if (!user.profile.emailAddress.includes('.com')) {
        errors.push({
          code: EMAIL_MX_CHECK_FAILED_ERROR,
          propertyPath: 'profile.emailAddress',
          message: 'This value is not a valid email address.'
        })
      } else if (!user.profile.emailAddress.includes('@sqills.com')) {
        errors.push({
          code: EMAIL_HOST_CHECK_FAILED_ERROR,
          propertyPath: 'profile.emailAddress',
          message: 'This value is not a valid email address.'
        })
      }
    }
    if (!user.profile.address.postalCode) {
      errors.push({
        code: VALUE_SHOULD_NOT_BE_BLANK,
        propertyPath: 'profile.address.postalCode',
        message: 'This value should not be blank'
      })
    } else if (user.profile.address.postalCode.length !== 6) {
      errors.push({
        code: VALUE_IS_INVALID,
        propertyPath: 'profile.address.postalCode',
        message: 'This value is not valid'
      })
    }
    if (!user.profile.address.houseNumber) {
      errors.push({
        code: VALUE_SHOULD_NOT_BE_BLANK,
        propertyPath: 'profile.address.houseNumber',
        message: 'This value should not be blank'
      })
    } else if (!RegExp('^[0-9]*$').test(String(user.profile.address.houseNumber))) {
      errors.push({
        code: VALUE_IS_INVALID,
        propertyPath: 'profile.address.houseNumber',
        message: 'This value is not valid'
      })
    }
    if (!user.profile.address.street) {
      errors.push({
        code: VALUE_SHOULD_NOT_BE_BLANK,
        propertyPath: 'profile.address.street',
        message: 'This value should not be blank'
      })
    } else if (!RegExp('^[\\w]*$').test(String(user.profile.address.street))) {
      errors.push({
        code: VALUE_IS_INVALID,
        propertyPath: 'profile.address.street',
        message: 'This value is not valid'
      })
    }
    if (!user.profile.address.city) {
      errors.push({
        code: VALUE_SHOULD_NOT_BE_BLANK,
        propertyPath: 'profile.address.city',
        message: 'This value should not be blank'
      })
    } else if (!RegExp('^[\\w\\s]*$').test(String(user.profile.address.city))) {
      errors.push({
        code: VALUE_IS_INVALID,
        propertyPath: 'profile.address.city',
        message: 'This value is not valid'
      })
    }
    if (errors.length) {
      return new Promise(resolve =>
        setTimeout(() => {
          resolve({
            'errors': errors
          })
        }, 500)
      )
    }
    return {}
  }

  async register (
    customerDetails: CustomerDetails, address: Address,
    credentials: AccountCredentials
  ): Promise<ValidationResult> {
    const errors = []
    if (!credentials.emailAddress) {
      errors.push({
        code: VALUE_SHOULD_NOT_BE_BLANK,
        propertyPath: 'account.emailAddress',
        message: 'This value should not be blank'
      })
    } else {
      if (!credentials.emailAddress.includes('@')) {
        errors.push({
          code: EMAIL_INVALID_FORMAT_ERROR,
          propertyPath: 'account.emailAddress',
          message: 'This value is not a valid email address.'
        })
      }
    }
    if (!credentials.password) {
      errors.push({
        code: VALUE_SHOULD_NOT_BE_BLANK,
        propertyPath: 'account.password',
        message: 'This value should not be blank'
      })
    } else {
      if (credentials.password.length < 5) {
        errors.push({
          code: VALUE_IS_INVALID,
          propertyPath: 'account.password',
          message: 'This value is not valid'
        })
      }
    }
    if (errors.length) {
      return new Promise(resolve =>
        setTimeout(() => {
          resolve({
            'errors': errors
          })
        }, 500)
      )
    }
    return {}
  }

  async validateRegistration (
    customerDetails: CustomerDetails, address: Address,
    credentials: AccountCredentials
  ): Promise<ValidationResult> {
    return this.register(customerDetails, address, credentials)
  }

  getJwtUser (token: string): User {
    return this.getUser()
  }

  async login (username: string, password: string, type: string): Promise<LoginResult> {
    return { token: '=NiC3+JwT+T0k3n' }
  }

  async requestPassword (emailAddress: string): Promise<void> {
  }

  async validateChangePasswordToken (token: string): Promise<void> {
  }

  async changePassword (token: string, newPassword: string): Promise<void> {
  }

  async getCustomerCards (): Promise<CustomerCards> {
    return [
      {engravedIdStrings: ['3528111122223333', '3528000011112222']}
    ]
  }
}

export default MockUserApiClient



// WEBPACK FOOTER //
// ./src/user-api/mock-user-api-client.js